/**
 * SchedulerJobsPanel - Table showing scheduled jobs with status, controls, and actions
 */
import { useState } from "react";
import { Play, ClockRewind, RefreshCw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { Toggle } from "@/components/base/toggle/toggle";
import { useSchedulerJobs, useToggleJob, useRunJobNow } from "@/hooks/api/use-scheduler-jobs";
import { JobHistoryModal } from "./job-history-modal";
import { toast } from "@/lib/toast-service";
import type { SchedulerJob } from "@/types/api/settings";
import { cx } from "@/utils/cx";

/**
 * Format interval for display
 */
function formatInterval(job: SchedulerJob): string {
    if (job.cron_expression) {
        return job.cron_expression;
    }
    if (job.interval_value) {
        return `${job.interval_value} ${job.interval_type}`;
    }
    return "-";
}

/**
 * Format relative time (e.g., "2 minutes ago")
 */
function formatRelativeTime(dateString?: string): string {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
}

/**
 * Status badge for job
 */
function StatusBadge({ job }: { job: SchedulerJob }) {
    if (!job.is_enabled) {
        return (
            <Badge color="gray" size="sm">
                Disabled
            </Badge>
        );
    }

    if (job.last_run_status === "running") {
        return (
            <Badge color="blue" size="sm">
                Running
            </Badge>
        );
    }

    if (job.last_run_status === "failed") {
        return (
            <Badge color="error" size="sm">
                Failed
            </Badge>
        );
    }

    return (
        <Badge color="success" size="sm">
            Active
        </Badge>
    );
}

/**
 * Last run info component
 */
function LastRunInfo({ job }: { job: SchedulerJob }) {
    const hasError = job.last_run_status === "failed" && job.last_error;

    return (
        <div className="flex flex-col">
            <span className={cx("text-sm", hasError ? "text-error-primary" : "text-secondary")}>
                {formatRelativeTime(job.last_run_at)}
            </span>
            {job.last_run_duration_ms && (
                <span className="text-xs text-tertiary">{Math.round(job.last_run_duration_ms / 1000)}s</span>
            )}
        </div>
    );
}

export function SchedulerJobsPanel() {
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

    const { data: jobs, isLoading, refetch } = useSchedulerJobs();
    const toggleJob = useToggleJob();
    const runJobNow = useRunJobNow();

    const handleToggleJob = async (job: SchedulerJob) => {
        try {
            await toggleJob.mutateAsync({ jobId: job.job_id, enabled: !job.is_enabled });
            toast.success(`Job ${job.is_enabled ? "disabled" : "enabled"}`);
        } catch (error) {
            toast.error("Failed to toggle job");
        }
    };

    const handleRunNow = async (job: SchedulerJob) => {
        try {
            await runJobNow.mutateAsync(job.job_id);
            toast.success(`Job "${job.display_name}" started`);
        } catch (error) {
            toast.error("Failed to run job");
        }
    };

    if (isLoading) {
        return (
            <div className="mt-6 pt-6 border-t border-secondary">
                <h4 className="text-sm font-semibold text-secondary uppercase tracking-wider mb-4">Scheduled Jobs</h4>
                <div className="flex items-center justify-center py-8 text-tertiary">Loading jobs...</div>
            </div>
        );
    }

    if (!jobs || jobs.length === 0) {
        return (
            <div className="mt-6 pt-6 border-t border-secondary">
                <h4 className="text-sm font-semibold text-secondary uppercase tracking-wider mb-4">Scheduled Jobs</h4>
                <div className="flex items-center justify-center py-8 text-tertiary">No scheduled jobs configured</div>
            </div>
        );
    }

    return (
        <div className="mt-6 pt-6 border-t border-secondary">
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-secondary uppercase tracking-wider">Scheduled Jobs</h4>
                <Button color="secondary" size="sm" iconLeading={RefreshCw01} onClick={() => refetch()}>
                    Refresh
                </Button>
            </div>

            <div className="rounded-lg ring-1 ring-secondary overflow-hidden bg-primary">
                <table className="w-full text-sm">
                    <thead className="border-b border-secondary bg-primary_alt">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium text-primary">Job</th>
                            <th className="px-4 py-3 text-left font-medium text-primary">Status</th>
                            <th className="px-4 py-3 text-left font-medium text-primary">Interval</th>
                            <th className="px-4 py-3 text-left font-medium text-primary">Last Run</th>
                            <th className="px-4 py-3 text-left font-medium text-primary">Stats</th>
                            <th className="px-4 py-3 text-right font-medium text-primary">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((job) => (
                            <tr key={job.job_id} className="border-t border-secondary hover:bg-primary_hover">
                                <td className="px-4 py-3">
                                    <div className="font-medium text-primary">{job.display_name}</div>
                                    <div className="text-xs text-tertiary font-mono">{job.job_id}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <StatusBadge job={job} />
                                </td>
                                <td className="px-4 py-3 text-secondary font-mono text-xs">{formatInterval(job)}</td>
                                <td className="px-4 py-3">
                                    <LastRunInfo job={job} />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="text-xs text-tertiary">
                                        <span className="text-success-primary">{job.success_count}</span>
                                        {" / "}
                                        <span className="text-error-primary">{job.failure_count}</span>
                                        {" / "}
                                        <span>{job.run_count}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2">
                                        <Toggle
                                            size="sm"
                                            isSelected={job.is_enabled}
                                            onChange={() => handleToggleJob(job)}
                                            isDisabled={toggleJob.isPending}
                                            aria-label={`Toggle ${job.display_name}`}
                                        />
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            iconLeading={Play}
                                            onClick={() => handleRunNow(job)}
                                            isDisabled={!job.is_enabled || runJobNow.isPending}
                                            aria-label={`Run ${job.display_name} now`}
                                        />
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            iconLeading={ClockRewind}
                                            onClick={() => setSelectedJobId(job.job_id)}
                                            aria-label={`View ${job.display_name} history`}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Job History Modal */}
            {selectedJobId && (
                <JobHistoryModal
                    jobId={selectedJobId}
                    jobName={jobs.find((j) => j.job_id === selectedJobId)?.display_name || selectedJobId}
                    isOpen={!!selectedJobId}
                    onClose={() => setSelectedJobId(null)}
                />
            )}
        </div>
    );
}
